import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Pencil,
  Trash2,
  Target,
  ArrowRightLeft,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { OutcomeForm } from "./OutcomeForm";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OutcomeRow {
  id: number;
  person_id: number;
  agency_id: number;
  category_id: number | null;
  outcome_type: string | null;
  outcome_value: string | null;
  outcome_date: string | null;
  created_by: number | null;
  category_name?: string;
  category_value_type?: string;
  author_first_name?: string;
  author_last_name?: string;
}

interface PrePostComparison {
  categoryName: string;
  categoryId: number;
  preOutcome: OutcomeRow | null;
  postOutcome: OutcomeRow | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface OutcomesListProps {
  personId: string;
}

const typeColors: Record<string, string> = {
  pre: "bg-blue-100 text-blue-800",
  post: "bg-green-100 text-green-800",
  milestone: "bg-amber-100 text-amber-800",
  other: "bg-gray-100 text-gray-700",
};

export function OutcomesList({ personId }: OutcomesListProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingOutcome, setEditingOutcome] = useState<OutcomeRow | null>(null);
  const [showComparison, setShowComparison] = useState(false);

  const { data: outcomes, isLoading } = useQuery<OutcomeRow[]>({
    queryKey: ["outcomes", personId],
    queryFn: async () => {
      const res = await api.get<{ data: OutcomeRow[] }>(
        `/clients/${personId}/outcomes`,
      );
      return res.data;
    },
    enabled: !!personId,
  });

  const { data: comparison } = useQuery<PrePostComparison[]>({
    queryKey: ["outcomes-comparison", personId],
    queryFn: async () => {
      const res = await api.get<{ data: PrePostComparison[] }>(
        `/clients/${personId}/outcomes/comparison`,
      );
      return res.data;
    },
    enabled: !!personId && showComparison,
  });

  const deleteMutation = useMutation({
    mutationFn: (outcomeId: number) =>
      api.delete(`/clients/${personId}/outcomes/${outcomeId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["outcomes", personId] });
      queryClient.invalidateQueries({
        queryKey: ["outcomes-comparison", personId],
      });
    },
  });

  function handleEdit(outcome: OutcomeRow) {
    setEditingOutcome(outcome);
    setShowForm(true);
  }

  function handleFormClose() {
    setShowForm(false);
    setEditingOutcome(null);
  }

  if (showForm) {
    return (
      <OutcomeForm
        personId={personId}
        outcome={editingOutcome}
        onClose={handleFormClose}
      />
    );
  }

  const outcomeList = outcomes ?? [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Outcomes</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => setShowComparison(!showComparison)}
          >
            <ArrowRightLeft className="h-4 w-4" />
            {showComparison ? "Hide Comparison" : "Pre/Post Comparison"}
          </Button>
          <Button
            size="sm"
            className="gap-2"
            onClick={() => setShowForm(true)}
          >
            <Plus className="h-4 w-4" />
            New Outcome
          </Button>
        </div>
      </div>

      {/* Pre/Post Comparison */}
      {showComparison && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Pre/Post Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            {!comparison || comparison.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No pre/post outcomes recorded yet. Record outcomes with type
                "Pre" or "Post" to see comparisons.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                      Category
                    </th>
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                      Pre (Intake)
                    </th>
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                      Post (Exit)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((comp) => (
                    <tr
                      key={comp.categoryId}
                      className="border-b last:border-0"
                    >
                      <td className="px-4 py-3 font-medium">
                        {comp.categoryName}
                      </td>
                      <td className="px-4 py-3">
                        {comp.preOutcome ? (
                          <div>
                            <span className="text-foreground">
                              {comp.preOutcome.outcome_value ?? "-"}
                            </span>
                            {comp.preOutcome.outcome_date && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                (
                                {new Date(
                                  comp.preOutcome.outcome_date,
                                ).toLocaleDateString()}
                                )
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {comp.postOutcome ? (
                          <div>
                            <span className="text-foreground">
                              {comp.postOutcome.outcome_value ?? "-"}
                            </span>
                            {comp.postOutcome.outcome_date && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                (
                                {new Date(
                                  comp.postOutcome.outcome_date,
                                ).toLocaleDateString()}
                                )
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="h-64 animate-pulse rounded-lg border bg-muted" />
      )}

      {/* Empty state */}
      {!isLoading && outcomeList.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Target className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No outcomes recorded yet.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Outcomes table */}
      {!isLoading && outcomeList.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Value
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Recorded By
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {outcomeList.map((outcome) => {
                  const authorName =
                    outcome.author_first_name && outcome.author_last_name
                      ? `${outcome.author_first_name} ${outcome.author_last_name}`
                      : "-";

                  return (
                    <tr
                      key={outcome.id}
                      className="border-b last:border-0 hover:bg-muted/30"
                    >
                      <td className="px-4 py-3">
                        {outcome.outcome_date
                          ? new Date(
                              outcome.outcome_date,
                            ).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="px-4 py-3">
                        {outcome.category_name ? (
                          <span className="inline-flex rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                            {outcome.category_name}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {outcome.outcome_type ? (
                          <span
                            className={cn(
                              "inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                              typeColors[outcome.outcome_type] ??
                                typeColors.other,
                            )}
                          >
                            {outcome.outcome_type}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 max-w-xs truncate">
                        {outcome.outcome_value ?? "-"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {authorName}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(outcome)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              if (
                                confirm(
                                  "Delete this outcome? This cannot be undone.",
                                )
                              ) {
                                deleteMutation.mutate(outcome.id);
                              }
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
